/**
 * CaregiverOrgBadge — small "Connected through {org}" banner shown at
 * the top of the caregiver dashboard when the user has an active
 * org_membership. Also surfaces a shortcut to the Share flow.
 *
 * Silent (returns null) when the caregiver isn't connected to an org.
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { HeartHandshake, ArrowRight } from "lucide-react";

export default function CaregiverOrgBadge() {
    const [org, setOrg] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let active = true;
        api.get("/org/submission-instructions/me").then(r => {
            if (!active) return;
            setOrg(r.data?.org || null);
        }).catch(() => { /* no org — silent */ })
            .finally(() => { if (active) setLoaded(true); });
        return () => { active = false; };
    }, []);

    if (!loaded || !org) return null;

    return (
        <div
            data-testid="dashboard-org-badge"
            className="rounded-2xl border border-[#D5E6E4] bg-[#EDF7F5] px-4 py-2.5 flex flex-wrap items-center justify-between gap-3"
        >
            <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-8 h-8 rounded-2xl bg-white flex items-center justify-center shrink-0">
                    <HeartHandshake size={15} className="text-teal-dark" />
                </span>
                <p className="text-sm text-[#1F2624] truncate">
                    Connected through <span className="font-semibold">{org.name}</span>
                </p>
            </div>
            <Link
                to="/care-summary"
                data-testid="dashboard-org-badge-share"
                className="inline-flex items-center gap-1 rounded-2xl bg-teal-dark text-white text-xs font-semibold px-4 py-1.5 min-h-[44px] hover:brightness-[1.05] transition"
            >
                Share an update <ArrowRight size={11} strokeWidth={2.4} />
            </Link>
        </div>
    );
}
